import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  Clock,
  CreditCard,
  LogOut,
  Package,
  Search,
  ShoppingCart,
  Ticket,
  TrendingUp,
  Wallet,
  XCircle,
  Zap,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { resolveBackendBaseUrl } from '../utils/runtimeConfig';

const API = `${resolveBackendBaseUrl()}/api`;

/* ────── Helpers ────── */

const formatCurrency = (amount) => {
  const n = Number(amount || 0);
  return `Rp ${n.toLocaleString('id-ID')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

const VOUCHER_STATUS_MAP = {
  tersedia: { label: 'Tersedia', color: 'emerald', icon: CheckCircle },
  terjual: { label: 'Terjual', color: 'amber', icon: ShoppingCart },
  aktif: { label: 'Aktif', color: 'cyan', icon: Zap },
  terpakai: { label: 'Terpakai', color: 'sky', icon: Zap },
  habis: { label: 'Habis', color: 'slate', icon: XCircle },
  expired: { label: 'Expired', color: 'rose', icon: XCircle },
  draft: { label: 'Draft', color: 'slate', icon: Clock },
};

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: TrendingUp },
  { key: 'vouchers', label: 'Voucher Saya', icon: Ticket },
  { key: 'commissions', label: 'Komisi', icon: Wallet },
];

/* ────── Stat Card ────── */

const StatCard = ({ icon: Icon, label, value, note, accentColor = 'cyan' }) => {
  const ringColor = {
    cyan: 'border-cyan-400/30 shadow-[0_0_28px_-16px_rgba(34,211,238,0.8)]',
    emerald: 'border-emerald-400/30 shadow-[0_0_28px_-16px_rgba(52,211,153,0.8)]',
    amber: 'border-amber-400/30 shadow-[0_0_28px_-16px_rgba(251,191,36,0.8)]',
    rose: 'border-rose-400/30 shadow-[0_0_28px_-16px_rgba(251,113,133,0.8)]',
  }[accentColor] || 'border-cyan-400/30 shadow-[0_0_28px_-16px_rgba(34,211,238,0.8)]';

  const iconBg = {
    cyan: 'border-cyan-300/35 bg-cyan-500/10',
    emerald: 'border-emerald-300/35 bg-emerald-500/10',
    amber: 'border-amber-300/35 bg-amber-500/10',
    rose: 'border-rose-300/35 bg-rose-500/10',
  }[accentColor] || 'border-cyan-300/35 bg-cyan-500/10';

  return (
    <div className={`rounded-2xl border bg-[linear-gradient(135deg,rgba(6,16,34,0.88),rgba(5,12,26,0.82))] backdrop-blur-md p-5 transition-all duration-300 hover:-translate-y-0.5 ${ringColor}`}>
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className={`h-10 w-10 rounded-xl border ${iconBg} flex items-center justify-center`}>
          {Icon && <Icon className="h-5 w-5 text-slate-200" />}
        </div>
      </div>
      <p className="text-xs uppercase tracking-[0.16em] text-cyan-200/80">{label}</p>
      <p className="mt-1 font-outfit text-3xl font-bold text-slate-50">{value}</p>
      {note && <p className="mt-1 text-sm text-slate-300">{note}</p>}
    </div>
  );
};

/* ────── Badge ────── */

const StatusBadge = ({ status }) => {
  const meta = VOUCHER_STATUS_MAP[status] || VOUCHER_STATUS_MAP.draft;
  const colorMap = {
    emerald: 'border-emerald-400/45 bg-emerald-500/20 text-emerald-200',
    amber: 'border-amber-400/45 bg-amber-500/20 text-amber-200',
    cyan: 'border-cyan-400/45 bg-cyan-500/20 text-cyan-200',
    sky: 'border-sky-400/45 bg-sky-500/20 text-sky-200',
    rose: 'border-rose-400/45 bg-rose-500/20 text-rose-200',
    slate: 'border-slate-400/45 bg-slate-500/20 text-slate-200',
  };

  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${colorMap[meta.color] || colorMap.slate}`}>
      <span className="h-1.5 w-1.5 rounded-full animate-pulse bg-current" />
      {meta.label}
    </span>
  );
};

/* ══════════════════════════════════════════════════════════════════
 * 🧑‍💼  Agent Portal — Dashboard, Vouchers, Commissions
 * ══════════════════════════════════════════════════════════════════ */

const AgentPortal = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [agent, setAgent] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ktc_agent') || '{}'); } catch { return {}; }
  });

  /* ── Data ── */
  const [summary, setSummary] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [voucherTotal, setVoucherTotal] = useState(0);
  const [commissions, setCommissions] = useState([]);
  const [commissionTotal, setCommissionTotal] = useState(0);
  const [commissionAmount, setCommissionAmount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [voucherSearch, setVoucherSearch] = useState('');
  const [voucherStatusFilter, setVoucherStatusFilter] = useState('all');

  /* ── Auth ── */
  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_agent_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('ktc_agent_token');
    localStorage.removeItem('ktc_agent');
    navigate('/agen/login');
  };

  /* ── Fetchers ── */
  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/agen/summary`, { headers: getHeaders() });
      setSummary(res.data);
    } catch { /* silent */ }
  }, [getHeaders]);

  const fetchVouchers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (voucherStatusFilter !== 'all') params.status = voucherStatusFilter;
      if (voucherSearch.trim()) params.q = voucherSearch.trim();
      const res = await axios.get(`${API}/agen/vouchers`, { headers: getHeaders(), params });
      setVouchers(Array.isArray(res.data?.items) ? res.data.items : []);
      setVoucherTotal(res.data?.total || 0);
    } catch { setVouchers([]); }
    finally { setLoading(false); }
  }, [getHeaders, voucherStatusFilter, voucherSearch]);

  const fetchCommissions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/agen/commissions`, { headers: getHeaders(), params: { limit: 500 } });
      setCommissions(Array.isArray(res.data?.items) ? res.data.items : []);
      setCommissionTotal(res.data?.total || 0);
      setCommissionAmount(res.data?.total_amount || 0);
    } catch { setCommissions([]); }
  }, [getHeaders]);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/agen/me`, { headers: getHeaders() });
      setAgent(res.data);
      localStorage.setItem('ktc_agent', JSON.stringify(res.data));
    } catch { /* silent */ }
  }, [getHeaders]);

  useEffect(() => {
    fetchProfile();
    fetchSummary();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'vouchers') fetchVouchers();
    if (activeTab === 'commissions') fetchCommissions();
  }, [activeTab, fetchVouchers, fetchCommissions]);

  /* ── Sell handler ── */
  const handleSellVoucher = async (voucherId) => {
    if (!window.confirm('Tandai voucher ini sebagai terjual?')) return;
    try {
      await axios.post(`${API}/agen/vouchers/${voucherId}/sell`, {}, { headers: getHeaders() });
      toast.success('Voucher berhasil ditandai terjual');
      fetchVouchers();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menjual voucher');
    }
  };

  /* ── Computed ── */
  const commissionStats = useMemo(() => {
    const pending = commissions.filter(c => c.status === 'pending').reduce((s, c) => s + Number(c.amount || 0), 0);
    const paid = commissions.filter(c => c.status === 'paid').reduce((s, c) => s + Number(c.amount || 0), 0);
    return { pending, paid };
  }, [commissions]);

  const selectClass = "h-10 rounded-xl border border-cyan-300/25 bg-[#081427] px-3 text-sm text-slate-100 outline-none focus:border-cyan-300/60 transition-all";

  /* ══════════════════════ RENDER ══════════════════════ */

  return (
    <div className="min-h-screen bg-[#050B14] text-slate-100">
      <main className="mx-auto max-w-6xl px-4 py-6 lg:py-8">

        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-emerald-300/80 mb-1">Portal Agen</p>
            <h1 className="text-2xl font-bold text-slate-50 font-outfit">Halo, {agent.name || 'Agen'} 👋</h1>
            <p className="text-sm text-slate-400 mt-0.5">Kode: <span className="text-emerald-300 font-mono">{agent.code || '-'}</span></p>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 rounded-xl border border-rose-400/35 bg-rose-500/10 px-4 py-2.5 text-sm font-semibold text-rose-200 hover:bg-rose-500/20 transition-all"
          >
            <LogOut className="w-4 h-4" />
            Keluar
          </button>
        </div>

        {/* ── Tab Bar ── */}
        <div className="rounded-2xl border border-cyan-400/30 bg-[linear-gradient(135deg,rgba(6,16,34,0.88),rgba(5,12,26,0.82))] backdrop-blur-md p-2 mb-6">
          <div className="flex gap-1 flex-wrap">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  activeTab === key
                    ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-300/45 shadow-[0_0_20px_-10px_rgba(52,211,153,0.9)]'
                    : 'text-slate-300 border border-transparent hover:bg-emerald-500/10 hover:border-emerald-300/25'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ══════════ DASHBOARD ══════════ */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard icon={Package} label="Total Voucher" value={summary?.total_vouchers ?? '...'} accentColor="cyan" />
              <StatCard icon={CheckCircle} label="Stok Tersedia" value={summary?.tersedia ?? '...'} note="siap jual" accentColor="emerald" />
              <StatCard icon={ShoppingCart} label="Terjual" value={summary?.terjual ?? '...'} accentColor="amber" />
            </section>
            <section className="grid gap-4 sm:grid-cols-3">
              <StatCard icon={CreditCard} label="Total Revenue" value={formatCurrency(summary?.total_revenue)} accentColor="cyan" />
              <StatCard icon={Clock} label="Komisi Pending" value={formatCurrency(summary?.commission_pending)} accentColor="amber" />
              <StatCard icon={Wallet} label="Komisi Dibayar" value={formatCurrency(summary?.commission_paid)} accentColor="emerald" />
            </section>

            {/* Quick info */}
            <div className="rounded-2xl border border-cyan-400/20 bg-[linear-gradient(135deg,rgba(6,16,34,0.88),rgba(5,12,26,0.82))] p-5">
              <h3 className="text-sm font-semibold text-emerald-200 uppercase tracking-[0.12em] mb-3">Info Agen</h3>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Nama</span>
                  <span className="text-slate-100 font-medium">{agent.name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Kode</span>
                  <span className="text-emerald-300 font-mono">{agent.code || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Telepon</span>
                  <span className="text-slate-100">{agent.phone || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Tipe Komisi</span>
                  <span className="text-slate-100">{agent.commission_type === 'percent' ? `${agent.commission_value}%` : formatCurrency(agent.commission_value)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════ VOUCHER SAYA ══════════ */}
        {activeTab === 'vouchers' && (
          <div className="rounded-2xl border border-cyan-400/30 bg-[linear-gradient(135deg,rgba(6,16,34,0.88),rgba(5,12,26,0.82))] backdrop-blur-md p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-sm font-semibold text-emerald-200 uppercase tracking-[0.12em]">Voucher Saya ({voucherTotal})</h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cyan-200/80" />
                  <input
                    value={voucherSearch}
                    onChange={(e) => setVoucherSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchVouchers()}
                    placeholder="Cari kode..."
                    className="h-10 w-48 rounded-xl border border-cyan-300/30 bg-[#081427] pl-9 pr-3 text-sm text-slate-100 placeholder:text-slate-400/80 outline-none focus:border-cyan-300/60 transition-all"
                  />
                </div>
                <select value={voucherStatusFilter} onChange={(e) => setVoucherStatusFilter(e.target.value)} className={selectClass + ' w-36'}>
                  <option value="all">Semua Status</option>
                  {Object.entries(VOUCHER_STATUS_MAP).map(([key, meta]) => (
                    <option key={key} value={key}>{meta.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-cyan-400/20">
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Kode</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Profil</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Harga</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Status</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Dibuat</th>
                    <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={6} className="text-center py-8 text-slate-400">Memuat...</td></tr>
                  )}
                  {!loading && vouchers.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-8 text-slate-500">Tidak ada voucher</td></tr>
                  )}
                  {vouchers.map((v) => (
                    <tr key={v.id || v.code} className="border-b border-cyan-400/10 hover:bg-emerald-500/5 transition-colors">
                      <td className="py-2.5 px-3 font-mono text-emerald-300 text-xs">{v.code}</td>
                      <td className="py-2.5 px-3 text-slate-200">{v.profile_name || '-'}</td>
                      <td className="py-2.5 px-3 text-slate-200">{formatCurrency(v.price)}</td>
                      <td className="py-2.5 px-3"><StatusBadge status={v.status} /></td>
                      <td className="py-2.5 px-3 text-slate-400 text-xs">{formatDate(v.created_at)}</td>
                      <td className="py-2.5 px-3">
                        {(v.status === 'tersedia' || v.status === 'draft') && (
                          <button
                            onClick={() => handleSellVoucher(v.id)}
                            className="inline-flex items-center gap-1 rounded-lg border border-amber-400/35 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-500/25 transition-all"
                          >
                            <ShoppingCart className="w-3.5 h-3.5" />
                            Jual
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {voucherTotal > 200 && (
              <p className="text-xs text-slate-500 mt-3 text-center">Menampilkan 200 dari {voucherTotal} voucher.</p>
            )}
          </div>
        )}

        {/* ══════════ KOMISI ══════════ */}
        {activeTab === 'commissions' && (
          <div className="space-y-5">
            {/* Summary Cards */}
            <section className="grid sm:grid-cols-3 gap-4">
              <StatCard icon={Clock} label="Komisi Pending" value={formatCurrency(commissionStats.pending)} accentColor="amber" />
              <StatCard icon={Wallet} label="Sudah Dibayar" value={formatCurrency(commissionStats.paid)} accentColor="emerald" />
              <StatCard icon={CreditCard} label="Total Transaksi" value={commissionTotal} accentColor="cyan" />
            </section>

            {/* Commission Table */}
            <div className="rounded-2xl border border-cyan-400/30 bg-[linear-gradient(135deg,rgba(6,16,34,0.88),rgba(5,12,26,0.82))] backdrop-blur-md p-5">
              <h2 className="text-sm font-semibold text-emerald-200 uppercase tracking-[0.12em] mb-4">Riwayat Komisi</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-cyan-400/20">
                      <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Voucher</th>
                      <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Komisi</th>
                      <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Status</th>
                      <th className="text-left py-2 px-3 text-xs text-cyan-200 uppercase">Tanggal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.length === 0 ? (
                      <tr><td colSpan={4} className="text-center py-8 text-slate-500">Belum ada komisi</td></tr>
                    ) : (
                      commissions.map((c, idx) => (
                        <tr key={c.id || idx} className="border-b border-cyan-400/10 hover:bg-emerald-500/5 transition-colors">
                          <td className="py-2.5 px-3 font-mono text-emerald-300 text-xs">{c.voucher_id?.slice(0, 8) || '-'}</td>
                          <td className="py-2.5 px-3 text-cyan-300 font-semibold">{formatCurrency(c.amount)}</td>
                          <td className="py-2.5 px-3">
                            <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                              c.status === 'paid'
                                ? 'border-emerald-400/45 bg-emerald-500/20 text-emerald-200'
                                : 'border-amber-400/45 bg-amber-500/20 text-amber-200'
                            }`}>
                              <span className="h-1.5 w-1.5 rounded-full animate-pulse bg-current" />
                              {c.status === 'paid' ? 'Dibayar' : 'Pending'}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-slate-400 text-xs">{formatDate(c.created_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AgentPortal;
